import type { Room } from '@/types/room';

/**
 * BFS sur le graphe de salles.
 * Retourne la liste d'IDs de salles à traverser (source exclue, destination incluse).
 */
export function findRoomPath(
  fromId: string,
  toId: string,
  rooms: Room[]
): string[] {
  if (fromId === toId) return [];

  const roomMap = new Map(rooms.map(r => [r.id, r]));
  const visited = new Set<string>([fromId]);
  const queue: Array<{ id: string; path: string[] }> = [{ id: fromId, path: [] }];

  while (queue.length > 0) {
    const { id, path } = queue.shift()!;
    const room = roomMap.get(id);
    if (!room) continue;

    for (const neighborId of room.adjacentRoomIds) {
      if (visited.has(neighborId)) continue;
      visited.add(neighborId);
      const newPath = [...path, neighborId];
      if (neighborId === toId) return newPath;
      queue.push({ id: neighborId, path: newPath });
    }
  }

  // Pas de chemin trouvé — retourner la destination directement
  return [toId];
}

/** Convertit un chemin de room IDs en liste de points pixels (centre de chaque salle) */
export function roomPathToPixels(
  roomIds: string[],
  rooms: Room[],
  agentId: string
): Array<{ x: number; y: number }> {
  const roomMap = new Map(rooms.map(r => [r.id, r]));
  return roomIds.map(id => {
    const room = roomMap.get(id);
    if (!room) return { x: 0, y: 0 };
    // Cherche un waypoint libre, sinon centre de la salle
    const freeWp = room.waypoints.find(wp => !wp.occupiedBy || wp.occupiedBy === agentId);
    if (freeWp) return { x: freeWp.x, y: freeWp.y };
    return { x: room.pixelX + room.pixelW / 2, y: room.pixelY + room.pixelH / 2 };
  });
}
